import { Controller } from '@hotwired/stimulus';
import Offcanvas from 'bootstrapV5/js/dist/offcanvas';
import consumer from '../channels/cable';
import { initSignature } from '../lib/universign';

export default class extends Controller {
  static targets = ['block', 'drawer', 'generationStep', 'signatureStep', 'successStep', 'errorStep'];
  static values = {
    universignEndpoint: String,
    createUrl: String,
    refreshUrl: String,
    type: String,
    iframeContainerId: String,
    successMessage: String,
  };

  connect() {
    this.boundOnMessage = this.#onPostMessage.bind(this);
    window.addEventListener('message', this.boundOnMessage);
  }

  disconnect() {
    window.removeEventListener('message', this.boundOnMessage);
    this.#unsubscribe();
    this.#getOffcanvas()?.hide();
  }

  // ACTIONS

  open(event) {
    event?.preventDefault();

    this.#showStep('generation');
    this.#getOffcanvas().show();

    fetch(this.createUrlValue, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '',
      },
      credentials: 'same-origin',
    })
      .then((response) => {
        if (!response.ok) throw new Error('tax-exemption-signature create returned ' + response.status);

        return response.json();
      })
      .then((data) => {
        if (data.error) {
          this.#showStep('error');
          window.toastr?.error(data.error);

          return;
        }

        this.#subscribe(data.id, data.signature_token);
      })
      .catch((err) => {
        console.error('Unable to start signature', err);
        this.#showStep('error');
      });
  }

  close(event) {
    event?.preventDefault();
    this.#getOffcanvas()?.hide();
    this.#unsubscribe();
  }

  // PRIVATE

  #subscribe(taxExemptionId, signatureToken) {
    this.#unsubscribe();

    this.subscription = consumer.subscriptions.create(
      {
        channel: 'TaxExemptionSignatureChannel',
        tax_exemption_type: this.typeValue,
        tax_exemption_id: taxExemptionId,
        signature_token: signatureToken,
      },
      {
        received: (data) => this.#onChannelReceived(data),
      }
    );
  }

  #unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  }

  #onChannelReceived(data) {
    if (data.status === 'success' && data.e_sign_id) {
      this.#showStep('signature');
      try {
        initSignature(this.iframeContainerIdValue, data.e_sign_id, this.universignEndpointValue);
      } catch (err) {
        console.error('Universign init failed', err);
        this.#showStep('error');
      }
    } else {
      this.#showStep('error');
    }
  }

  #onPostMessage(event) {
    if (event.origin !== window.location.origin) return;
    if (event.data?.type !== 'universign:complete') return;

    this.#unsubscribe();

    if (event.data.status === 'success') {
      this.#showStep('success');
      this.#refreshBlock();
    } else {
      this.#showStep('error');
    }
  }

  #refreshBlock() {
    fetch(this.refreshUrlValue, {
      headers: { 'Accept': 'text/html' },
      credentials: 'same-origin',
    })
      .then((r) => {
        if (!r.ok) throw new Error(`refresh status ${r.status}`);

        return r.text();
      })
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newBlock = doc.querySelector('[data-tax-exemption-signature-target="block"]');
        if (newBlock && this.hasBlockTarget) {
          this.blockTarget.replaceWith(newBlock);
          window.toastr?.success(this.successMessageValue);
        }
      })
      .catch((err) => {
        console.error('Refresh block failed', err);
        window.toastr?.info('Votre attestation a été signée. Veuillez rafraîchir la page.');
      });
  }

  #showStep(stepName) {
    const steps = {
      generation: this.hasGenerationStepTarget && this.generationStepTarget,
      signature: this.hasSignatureStepTarget && this.signatureStepTarget,
      success: this.hasSuccessStepTarget && this.successStepTarget,
      error: this.hasErrorStepTarget && this.errorStepTarget,
    };

    Object.entries(steps).forEach(([name, el]) => {
      if (!el) return;
      if (name === stepName) {
        el.classList.remove('d-none');
      } else {
        el.classList.add('d-none');
      }
    });
  }

  #getOffcanvas() {
    if (!this.hasDrawerTarget) return null;

    return Offcanvas.getOrCreateInstance(this.drawerTarget);
  }
}
